import React from 'react';
import {
  Alert, Button, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogTitle, FormControlLabel, Stack, Switch, Typography,
} from '@mui/material';
import DescriptionIcon from '@mui/icons-material/Description';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import {
  buildWarbandExport,
  collectPortraits,
  downloadMarkdown,
  openExportPreview,
  renderExportHtml,
  renderExportMarkdown,
  sanitizeFilename,
} from '../../services/exportService';

interface ExportWarbandModalProps {
  open: boolean;
  warbandId: string | null;
  warbandName: string;
  onClose: () => void;
}

export const ExportWarbandModal: React.FC<ExportWarbandModalProps> = ({ open, warbandId, warbandName, onClose }) => {
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [includeGamePages, setIncludeGamePages] = React.useState(false);

  const runExport = async (format: 'markdown' | 'pdf') => {
    if (!warbandId) return;
    setBusy(true);
    setError(null);
    try {
      const model = await buildWarbandExport(warbandId, { includeGamePages });
      if (format === 'markdown') {
        downloadMarkdown(`${sanitizeFilename(model.title)}.md`, renderExportMarkdown(model));
      } else {
        const portraits = await collectPortraits(warbandId, model);
        openExportPreview(renderExportHtml(model, portraits));
      }
      onClose();
    } catch (exportError) {
      setError(exportError instanceof Error ? exportError.message : 'Export failed.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Export {warbandName}</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Markdown downloads a .md file. The printable display opens in a new tab — print it (or save as PDF) from there.
        </Typography>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <FormControlLabel
          sx={{ mb: 1.5 }}
          control={<Switch checked={includeGamePages} onChange={(event) => setIncludeGamePages(event.target.checked)} />}
          label="Include pre-game and post-game pages"
        />
        <Stack spacing={1.5}>
          <Button
            variant="outlined"
            startIcon={busy ? <CircularProgress size={18} /> : <DescriptionIcon />}
            disabled={busy}
            onClick={() => void runExport('markdown')}
          >
            Save as Markdown
          </Button>
          <Button
            variant="outlined"
            startIcon={busy ? <CircularProgress size={18} /> : <PictureAsPdfIcon />}
            disabled={busy}
            onClick={() => void runExport('pdf')}
          >
            Pretty Display For Printing
          </Button>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};
